import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, X, Sparkles, LayoutDashboard, Calculator, CircleDollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "demoTourDismissed_v1";
const DEMO_EMAIL = "demo@cashflowpro.com";

const STEPS = [
  {
    path: "/app",
    icon: LayoutDashboard,
    label: "Step 1 of 4 · Overview",
    title: "Your cash command center",
    body: "Net YTD, runway, monthly flow, top expense categories — everything in one glance. Real numbers, real-time.",
    next: "/app/transactions",
    nextLabel: "See transactions",
  },
  {
    path: "/app/transactions",
    icon: LayoutDashboard,
    label: "Step 2 of 4 · Transactions",
    title: "Every dollar accounted for",
    body: "Manual entry, CSV import, or drop in a bank statement PDF — BizCashflow Pros extracts the transactions and flags deductibles automatically.",
    next: "/app/tax",
    nextLabel: "See tax savings",
  },
  {
    path: "/app/tax",
    icon: Calculator,
    label: "Step 3 of 4 · Tax savings",
    title: "Keep what's yours",
    body: "Live deductions, estimated quarterly tax, missed write-off opportunities. Most owners find $4,800+ in their first month.",
    next: "/app/debts",
    nextLabel: "See debt plan",
  },
  {
    path: "/app/debts",
    icon: CircleDollarSign,
    label: "Step 4 of 4 · Debt + AI",
    title: "Get out of debt — and ask anything",
    body: "Compare avalanche vs snowball. Then talk to Claude Sonnet 4.5 or GPT-5.2 — they know your numbers.",
    next: "/app/advisor",
    nextLabel: "Meet the AI advisor",
  },
];

const FINAL = {
  path: "/app/advisor",
  icon: Sparkles,
  label: "You're all set",
  title: "Like what you saw?",
  body: "Spin up your own workspace in 30 seconds. Free to start. No credit card.",
};

export default function DemoTour() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");

  if (!user || user.email !== DEMO_EMAIL || dismissed) return null;

  const inApp = loc.pathname.startsWith("/app");
  if (!inApp) return null;

  const step = STEPS.find((s) => s.path === loc.pathname);
  const onAdvisor = loc.pathname === "/app/advisor";
  const card = step || (onAdvisor ? FINAL : null);
  if (!card) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  const handleNext = () => {
    if (step?.next) nav(step.next);
  };

  const Icon = card.icon;

  return (
    <div
      data-testid="demo-tour-card"
      className="fixed z-40 bottom-6 right-6 w-80 max-w-[calc(100vw-2rem)] bg-white border border-forest/20 rounded-2xl shadow-lg overflow-hidden animate-fade-up"
      style={{ animationDelay: "0.2s" }}
    >
      <div className="bg-forest text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-medium">{card.label}</div>
        </div>
        <button onClick={dismiss} className="text-white/70 hover:text-white p-1" data-testid="demo-tour-dismiss" aria-label="Dismiss tour">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-medium mb-2 leading-snug" data-testid="demo-tour-title">{card.title}</h3>
        <p className="text-sm text-ink-light leading-relaxed mb-5">{card.body}</p>
        <div className="flex items-center justify-between gap-3">
          <button onClick={dismiss} className="text-xs text-ink-lighter hover:text-ink" data-testid="demo-tour-skip">
            Skip tour
          </button>
          {step ? (
            <button onClick={handleNext} data-testid="demo-tour-next"
              className="btn-forest text-sm inline-flex items-center gap-1.5 px-4 py-2">
              {step.nextLabel} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <Link to="/register" onClick={dismiss} data-testid="demo-tour-cta-signup"
              className="btn-forest text-sm inline-flex items-center gap-1.5 px-4 py-2">
              Create my account <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
